/**
 * Auth declaw shadow diagnostics + silent recovery orchestration.
 * Shadow logging is ALWAYS on; behavior suppression is flag-gated in authDeclaw.ts.
 * auth_family_revoked + suppression → persistent service-session banner (#099).
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  getElapsedSessionMs,
  isAuthDeclawEnabled,
  isReturningUser,
  shouldDeclawInvoluntaryLogout,
} from './authDeclaw';
import { readLocalStorageRefreshFp, readAuthJsonFpSnapshot } from './authDiagnostics';
import {
  classifyTerminalAuthVerdict,
  markSilentAuthRecoverySettled,
  observeInvoluntaryLogoutTerminal,
  resetAuthDeclawDeadFamilyForTests,
} from './authDeclawDeadFamily';
import {
  isRemoteAccessRevocationPayload,
  markServiceSessionDead,
  type RemoteAccessRevokedPayload,
} from './serviceSessionDead';
import {
  hasReachedUsableAuthedState,
  isBootTimeTerminalRejection,
  latchTerminalAuthFamily,
  maybeLatchFromRefreshRejection,
  resetAuthTerminalLatchForTests,
  shouldSuppressAutomaticRefresh,
} from './authTerminalLatch';

export const STREAMING_RESTORE_BANNER_MESSAGE =
  'Your session expired — sign in again to restore streaming';

export type AuthDeclawShadowResult = {
  suppression_applied: boolean;
  routed_to: 'service_session_dead' | 'full_session_dead' | 'boot_sign_in_required';
};

export type AuthDeclawPath =
  | 'api_refresh_rejected'
  | 'api_force_signout'
  | 'session_mark_dead'
  | 'session_expired_event'
  | 'reauth_required_panel'
  | 'app_layout_auth_gate';

export type AuthDeclawShadowEntry = {
  kind: 'auth_declaw_would_logout';
  path: AuthDeclawPath;
  trigger: string;
  declaw_flag_enabled: boolean;
  returning_user: boolean;
  suppression_applied: boolean;
  localStorage_fp: string;
  auth_json_fp: string;
  auth_json_mtime: string;
  elapsed_session_ms: number | null;
  silent_retry_recovered: boolean | null;
  playback: {
    state: string;
    zone_name: string;
    renderer_id: string | null;
  };
  extras?: Record<string, unknown>;
};

let _syncDegraded = false;
let _recoveryPromise: Promise<boolean> | null = null;
let _lastShadowKey = '';

function asRecord(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
}

/** Refresh rejection or connector payload carries auth_family_revoked. */
export function isAuthFamilyRevokedPayload(data: unknown): boolean {
  const obj = asRecord(data);
  if (!obj) return false;
  if (isRemoteAccessRevocationPayload(obj)) return true;
  const trigger = String(obj.trigger ?? '').toLowerCase();
  const reason = String(obj.reason ?? obj.error ?? '').toLowerCase();
  const msg = String(obj.error_description ?? obj.message ?? obj.user_message ?? '').toLowerCase();
  return (
    trigger.includes('auth_family')
    || reason.includes('auth_family')
    || msg.includes('auth_family_revoked')
    || msg.includes('sign in again to restore remote access')
    || msg.includes('sign in again to restore streaming')
  );
}

function normalizeDeclawPayload(data: unknown): RemoteAccessRevokedPayload {
  const obj = asRecord(data) ?? {};
  const trigger = String(obj.trigger ?? '').trim();
  const reason = String(obj.reason ?? obj.error ?? '').trim();
  const userMessage = String(obj.user_message ?? obj.error_description ?? '').trim();
  return {
    trigger: trigger || 'auth_family_revoked',
    reason: reason || 'auth_family_revoked',
    remote_access_affected: true,
    user_message: userMessage || STREAMING_RESTORE_BANNER_MESSAGE,
  };
}

/**
 * When auth_family_revoked, mark service-session-dead (banner) instead of
 * full web sign-out — but never during cold boot before usable authed state.
 * Returns suppression_applied=true only on the mid-session declaw path.
 */
export function applyAuthDeclawShadow(
  data: unknown,
  caller: string,
): AuthDeclawShadowResult {
  if (!isAuthFamilyRevokedPayload(data)) {
    return { suppression_applied: false, routed_to: 'full_session_dead' };
  }

  const payload = normalizeDeclawPayload(data);
  latchTerminalAuthFamily('family_revoked');

  if (!hasReachedUsableAuthedState()) {
    void shadowLogWouldBeLogout(
      'session_mark_dead',
      caller,
      { route: 'boot_sign_in_required', payload },
      null,
      false,
    );
    try {
      diagnosticLog('warn', 'other', 'auth_declaw_shadow_applied', {
        caller,
        trigger: payload.trigger ?? null,
        suppression_applied: false,
        route: 'boot_sign_in_required',
      });
    } catch { /* diagnostic only */ }
    return { suppression_applied: false, routed_to: 'boot_sign_in_required' };
  }

  markServiceSessionDead(payload.trigger || 'auth_family_revoked', payload);

  try {
    diagnosticLog('warn', 'other', 'auth_declaw_shadow_applied', {
      caller,
      trigger: payload.trigger ?? null,
      suppression_applied: true,
    });
  } catch { /* diagnostic only */ }

  return { suppression_applied: true, routed_to: 'service_session_dead' };
}

/** Stream-url / API 401 surfaces the same banner when refresh family is dead. */
export function markStreamingAuthFailureFromHttp(status: number, bodyText?: string): boolean {
  if (status !== 401 && status !== 403) return false;
  applyAuthDeclawShadow(
    {
      trigger: 'auth_family_revoked',
      user_message: STREAMING_RESTORE_BANNER_MESSAGE,
      remote_access_affected: true,
      error_description: bodyText,
    },
    'stream_auth_failure',
  );
  return true;
}

async function readPlaybackSnapshot(): Promise<AuthDeclawShadowEntry['playback']> {
  try {
    const { useMaestroStore } = await import('../store/maestroStore');
    const s = useMaestroStore.getState();
    return {
      state: String(s.playbackState || 'unknown'),
      zone_name: String(s.system?.zoneName || ''),
      renderer_id: s.activeConnectorRenderer?.id
        ? String(s.activeConnectorRenderer.id)
        : null,
    };
  } catch {
    return { state: 'unknown', zone_name: '', renderer_id: null };
  }
}

async function buildShadowEntry(
  path: AuthDeclawPath,
  trigger: string,
  suppressionApplied: boolean,
  silentRetryRecovered: boolean | null,
  extras?: Record<string, unknown>,
): Promise<AuthDeclawShadowEntry> {
  const jsonSnap = await readAuthJsonFpSnapshot().catch(() => ({
    fp: 'read_failed',
    mtime: 'read_failed',
  }));
  return {
    kind: 'auth_declaw_would_logout',
    path,
    trigger,
    declaw_flag_enabled: isAuthDeclawEnabled(),
    returning_user: isReturningUser(),
    suppression_applied: suppressionApplied,
    localStorage_fp: readLocalStorageRefreshFp(),
    auth_json_fp: jsonSnap.fp,
    auth_json_mtime: jsonSnap.mtime,
    elapsed_session_ms: getElapsedSessionMs(),
    silent_retry_recovered: silentRetryRecovered,
    playback: await readPlaybackSnapshot(),
    ...(extras ? { extras } : {}),
  };
}

function emitShadowEntry(entry: AuthDeclawShadowEntry): void {
  const dedupeKey = `${entry.path}::${entry.trigger}::${entry.localStorage_fp}`;
  if (dedupeKey === _lastShadowKey) return;
  _lastShadowKey = dedupeKey;
  try {
    diagnosticLog('warn', 'other', 'auth_declaw_would_logout', entry as unknown as Record<string, unknown>);
  } catch { /* never propagate */ }
}

export async function shadowLogWouldBeLogout(
  path: AuthDeclawPath,
  trigger: string,
  extras?: Record<string, unknown>,
  silentRetryRecovered: boolean | null = null,
  suppressionApplied = false,
): Promise<void> {
  try {
    const entry = await buildShadowEntry(path, trigger, suppressionApplied, silentRetryRecovered, extras);
    emitShadowEntry(entry);
  } catch { /* never propagate */ }
}

export function isAuthDeclawSyncDegraded(): boolean {
  return _syncDegraded;
}

export function getAuthDeclawReconnectNudge(): string | null {
  if (!_syncDegraded || !shouldDeclawInvoluntaryLogout()) return null;
  return 'Reconnect to sync your library and favorites.';
}

function setAuthDeclawSyncDegraded(degraded: boolean): void {
  _syncDegraded = degraded;
}

export function scheduleSilentAuthRecovery(source: string): void {
  if (shouldSuppressAutomaticRefresh() || isBootTimeTerminalRejection()) return;
  if (_recoveryPromise) return;
  _recoveryPromise = runSilentAuthRecovery(source).finally(() => {
    _recoveryPromise = null;
  });
}

/** Await the in-flight silent recovery pass (no-op when none scheduled). */
export async function awaitSilentAuthRecovery(): Promise<boolean> {
  if (!_recoveryPromise) return false;
  return _recoveryPromise;
}

async function runSilentAuthRecovery(source: string): Promise<boolean> {
  try {
    const { refreshAccessTokenShared } = await import('../services/api');
    const result = await refreshAccessTokenShared(`authDeclaw:${source}`);
    if (result.accessToken && result.refreshToken) {
      setAuthDeclawSyncDegraded(false);
      markSilentAuthRecoverySettled(true);
      try {
        window.dispatchEvent(new CustomEvent('maestro:token-refreshed', {
          detail: {
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          },
        }));
        window.dispatchEvent(new CustomEvent('maestro:declaw-shell-restore'));
      } catch { /* ignore */ }
      return true;
    }
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const parsed = JSON.parse(stored) as { accessToken?: string; refreshToken?: string };
      if (parsed.accessToken && parsed.refreshToken) {
        setAuthDeclawSyncDegraded(false);
        markSilentAuthRecoverySettled(true);
        try {
          window.dispatchEvent(new CustomEvent('maestro:declaw-shell-restore'));
        } catch { /* ignore */ }
        return true;
      }
    }
    setAuthDeclawSyncDegraded(true);
    markSilentAuthRecoverySettled(false);
    return false;
  } catch {
    setAuthDeclawSyncDegraded(true);
    markSilentAuthRecoverySettled(false);
    return false;
  }
}

/**
 * Shadow-log every would-be logout; suppress terminal logout when declaw applies.
 * Returns true when the caller should skip logout / unmount side effects.
 */
export function interceptInvoluntaryLogout(
  path: AuthDeclawPath,
  trigger: string,
  extras?: Record<string, unknown>,
): boolean {
  const payload = extras?.payload as { reason?: string } | undefined;
  const payloadReason = String(payload?.reason ?? '').toLowerCase();
  const terminalFromRefresh = path === 'api_refresh_rejected'
    ? maybeLatchFromRefreshRejection(extras?.status, extras?.data)
    : null;
  const isFamilyRevoked = terminalFromRefresh === 'family_revoked'
    || payloadReason === 'family_revoked'
    || trigger.toLowerCase().includes('auth_family_revoked')
    || trigger.toLowerCase().includes('family_revoked');

  if (isFamilyRevoked) {
    latchTerminalAuthFamily('family_revoked');
  } else if (path === 'reauth_required_panel' && payloadReason) {
    const verdict = classifyTerminalAuthVerdict(401, { error_description: payloadReason });
    if (verdict) latchTerminalAuthFamily(verdict);
  }

  if (isBootTimeTerminalRejection()) {
    observeInvoluntaryLogoutTerminal(path, extras);
    void shadowLogWouldBeLogout(path, trigger, extras, null, false);
    return false;
  }

  const suppress = shouldDeclawInvoluntaryLogout();
  observeInvoluntaryLogoutTerminal(path, extras);
  void shadowLogWouldBeLogout(path, trigger, extras, null, suppress);
  if (suppress) {
    const payload = extras?.data ?? extras;
    if (isAuthFamilyRevokedPayload(payload)) {
      applyAuthDeclawShadow(payload, `${path}:${trigger}`);
    } else if (!shouldSuppressAutomaticRefresh()) {
      scheduleSilentAuthRecovery(`${path}:${trigger}`);
    }
  }
  return suppress;
}

export function resetAuthDeclawShadowForTests(): void {
  _syncDegraded = false;
  _recoveryPromise = null;
  _lastShadowKey = '';
  resetAuthDeclawDeadFamilyForTests();
  resetAuthTerminalLatchForTests();
}
